import utils from "../universal/utils";

const basePath = utils.getEdgeBasePath();

/**
 * Client helpers for the opt-in browser push notification (VAPID Web Push).
 *
 * This exists mainly for Steam-only sign-ins with no email on file: it's the
 * one channel we can use to reach them (e.g. "your profile is verified")
 * without requiring an email address.
 */

export function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermissionState() {
  if (typeof window === "undefined" || !("Notification" in window)) return null;
  return Notification.permission;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerServiceWorker() {
  if (!isSupported()) return null;
  return navigator.serviceWorker.register("/sw.js");
}

async function fetchVapidPublicKey() {
  const res = await fetch(`${basePath}/api/v1/push/vapid-public-key`, {
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok || !data?.publicKey) {
    throw new Error(data?.message || "Push notifications are not available right now");
  }
  return data.publicKey;
}

/**
 * Requests browser notification permission (if needed), registers the
 * service worker, subscribes to push, and saves the subscription server-side.
 * Throws on failure so callers can surface a message; never call this
 * without explicit user intent (a button click).
 */
export async function subscribeToPush() {
  if (!isSupported()) {
    throw new Error("Push notifications aren't supported in this browser.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const registration = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
  const publicKey = await fetchVapidPublicKey();

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const res = await fetch(`${basePath}/api/v1/push/subscribe`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    throw new Error(data?.message || "Failed to save your notification subscription");
  }

  return subscription;
}

export async function unsubscribeFromPush() {
  if (!isSupported()) return;

  const registration = await navigator.serviceWorker.getRegistration("/sw.js");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => {});

  await fetch(`${basePath}/api/v1/push/unsubscribe`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint }),
  }).catch(() => {});
}
